import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Gauge, Home, Laptop, Car, Users, Megaphone, Settings, Landmark,
  Wallet, TrendingUp, TrendingDown, AlertTriangle, Plus, Trash2, Save,
  ChevronDown, ChevronRight, Check, HelpCircle, Minus, Clock, RefreshCw, Pencil, FileUp, Paperclip, X,
} from "lucide-react";

const EUR = (n) => (Number(n || 0)).toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const CAT_ICONS = { Structure: Home, Outils: Laptop, "Véhicule": Car, Personnel: Users, Commercial: Megaphone, Fonctionnement: Settings, "Fiscalité": Landmark };
const CATS = ["Structure", "Outils", "Véhicule", "Personnel", "Commercial", "Fonctionnement", "Fiscalité"];
const REC_FREQS = ["mensuel", "trimestriel", "annuel"];
const STATUTS = { renseigne: { l: "✓ Renseigné", c: "text-emerald-700 bg-emerald-50" }, a_verifier: { l: "? À vérifier", c: "text-amber-700 bg-amber-50" }, non_concerne: { l: "— Non concerné", c: "text-stone-400 bg-stone-100" } };

export default function Pilotage() {
  const [entite, setEntite] = useState("ppd");
  const [cockpit, setCockpit] = useState(null);
  const [charges, setCharges] = useState([]);
  const [settings, setSettings] = useState({ solde_tresorerie: 0, ca_mois_manuel: 0 });
  const [showCharges, setShowCharges] = useState(false);
  const [showPonctuelles, setShowPonctuelles] = useState(false);
  const [loading, setLoading] = useState(true);
  const [projHorizon, setProjHorizon] = useState(6);
  const [factureImport, setFactureImport] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadingFacture, setUploadingFacture] = useState(false);
  const [qontoLoading, setQontoLoading] = useState(false);
  const [qontoInfo, setQontoInfo] = useState(null);
  const autoSynced = useRef(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [c, ch, s] = await Promise.all([
        api.get("/pilotage/cockpit", { params: { entite } }),
        api.get("/pilotage/charges", { params: { entite } }),
        api.get("/pilotage/settings", { params: { entite } }),
      ]);
      setCockpit(c.data); setCharges(ch.data); setSettings(s.data);
    } finally { if (!silent) setLoading(false); }
  }, [entite]);
  useEffect(() => { load(); }, [load]);

  const syncQonto = async (silent = false) => {
    setQontoLoading(true);
    try {
      const { data } = await api.get("/pilotage/qonto/balance");
      if (data.balance === null || data.balance === undefined) {
        if (!silent) toast.error("Comptes multi-devises — solde à saisir manuellement");
        return;
      }
      setQontoInfo({ balance: data.balance, fetched_at: data.fetched_at });
      await saveSettings({ solde_tresorerie: data.balance });
      if (!silent) toast.success(`Trésorerie Qonto synchronisée : ${EUR(data.balance)}`);
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Échec de la synchronisation Qonto");
    } finally {
      setQontoLoading(false);
    }
  };

  // Auto-sync Qonto à l'ouverture (PPD), une seule fois par montage.
  useEffect(() => {
    if (entite === "ppd" && !autoSynced.current) {
      autoSynced.current = true;
      syncQonto(true);
    }
  }, [entite]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveSettings = async (patch) => { const { data } = await api.put("/pilotage/settings", { entite, ...settings, ...patch }); setSettings(data); load(true); };
  const addCharge = async () => { await api.post("/pilotage/charges", { entite, nom: "Nouvelle charge", categorie: "Fonctionnement", montant: 0, frequence: "mensuel", type: "fixe", statut: "a_verifier" }); loadCharges(); };
  const addPonctuelle = async () => { await api.post("/pilotage/charges", { entite, nom: "Nouvelle charge ponctuelle", categorie: "Fonctionnement", montant: 0, frequence: "ponctuel", type: "variable", statut: "a_verifier" }); loadCharges(); };
  const loadCharges = async () => { const { data } = await api.get("/pilotage/charges", { params: { entite } }); setCharges(data); };
  const updCharge = (id, k, v) => setCharges(p => p.map(c => c.id === id ? { ...c, [k]: v } : c));
  const saveCharge = async (c) => { await api.put(`/pilotage/charges/${c.id}`, c); toast.success("Charge enregistrée"); load(true); };
  const delCharge = async (id) => { if (!window.confirm("Supprimer cette charge ?")) return; await api.delete(`/pilotage/charges/${id}`); load(true); };

  const uploadFacture = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Merci de déposer un PDF"); return; }
    setUploadingFacture(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/pilotage/charges/import-facture/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFactureImport(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lecture de la facture impossible");
    } finally { setUploadingFacture(false); }
  };

  const viewFacture = async (cid) => {
    try {
      const res = await api.get(`/pilotage/charges/${cid}/facture`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Facture indisponible"); }
  };

  const projFull = cockpit?.projection || [];
  const proj = projFull.slice(0, projHorizon);
  const vals = proj.map(p => p.tresorerie);
  const minV = Math.min(0, ...vals), maxV = Math.max(1, ...vals);
  const pointY = (v) => 90 - ((v - minV) / (maxV - minV || 1)) * 80;
  const px = (i) => proj.length <= 1 ? 200 : 20 + i * (360 / (proj.length - 1));

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto" data-testid="pilotage-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Gauge className="text-[var(--ppd)]"/> Pilotage</h1>
          <p className="text-sm text-stone-500">Cockpit financier — santé actuelle & future en un coup d'œil.</p>
        </div>
        <div className="flex bg-stone-100 rounded-full p-1" data-testid="pilotage-tabs">
          {[["ppd", "Pourquoi Pas Design"], ["franchimob", "Franchi'Mob"]].map(([k, l]) => (
            <button key={k} onClick={() => setEntite(k)} data-testid={`pilotage-tab-${k}`} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${entite === k ? "bg-[var(--ppd)] text-white" : "text-stone-500"}`}>{l}</button>
          ))}
        </div>
      </div>

      {loading || !cockpit ? <div className="text-stone-400 text-sm py-20 text-center">Chargement…</div> : (<>
        {/* AUJOURD'HUI */}
        <section>
          <h2 className="text-xs font-bold uppercase tracking-wide text-stone-400 mb-2">Aujourd'hui</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-white border border-stone-200 rounded-xl p-4" data-testid="pilotage-tresorerie">
              <div className="flex items-center justify-between gap-1.5 text-xs text-stone-500 mb-1">
                <span className="flex items-center gap-1.5"><Wallet size={14}/> Trésorerie disponible</span>
                {entite === "ppd" && (
                  <button onClick={() => syncQonto(false)} disabled={qontoLoading} data-testid="pilotage-qonto-sync" title="Synchroniser le solde depuis Qonto" className="flex items-center gap-1 text-[10px] font-semibold text-[var(--ppd)] hover:underline disabled:opacity-50">
                    <RefreshCw size={12} className={qontoLoading ? "animate-spin" : ""}/> Qonto
                  </button>
                )}
              </div>
              <input type="number" defaultValue={settings.solde_tresorerie} key={settings.solde_tresorerie} onBlur={e => e.target.value != settings.solde_tresorerie && saveSettings({ solde_tresorerie: Number(e.target.value) })} className="text-2xl font-bold w-full outline-none bg-transparent" data-testid="pilotage-tresorerie-input"/>
              <div className="text-[10px] text-stone-400" data-testid="pilotage-qonto-info">
                {entite === "ppd"
                  ? (qontoInfo?.fetched_at ? `Qonto · maj ${new Date(qontoInfo.fetched_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}` : (qontoLoading ? "Synchronisation Qonto…" : "sync Qonto ou saisie manuelle"))
                  : "saisie manuelle"}
              </div>
            </div>
            <Big label="CA encaissé" icon={TrendingUp} value={cockpit.aujourdhui.ca_encaisse} color="text-emerald-600" testid="pilotage-ca-encaisse"/>
            <Big label="À encaisser" icon={Clock} value={cockpit.aujourdhui.a_encaisser} color="text-blue-600" testid="pilotage-a-encaisser"/>
            <Big label="À sortir (30 j)" icon={TrendingDown} value={cockpit.aujourdhui.a_sortir_30j} color="text-rose-600" testid="pilotage-a-sortir"/>
          </div>
          {entite === "franchimob" && (
            <div className="mt-3 bg-white border border-stone-200 rounded-xl p-4 flex items-center gap-3 flex-wrap" data-testid="pilotage-fmob-ca">
              <span className="text-sm text-stone-500">CA du mois (Franchi'Mob) :</span>
              <input type="number" defaultValue={settings.ca_mois_manuel} key={"ca" + settings.ca_mois_manuel} onBlur={e => e.target.value != settings.ca_mois_manuel && saveSettings({ ca_mois_manuel: Number(e.target.value) })} className="border border-stone-300 rounded px-3 py-1.5 text-sm w-40" data-testid="pilotage-fmob-ca-input"/>
            </div>
          )}
        </section>

        {/* TEMPS DE GESTION D'ENTREPRISE (PPD) */}
        {entite === "ppd" && (
          <GestionEntreprise gestion={cockpit.temps_gestion} onChanged={load} />
        )}

        {/* PROJECTION */}
        <section className="bg-white border border-stone-200 rounded-xl p-4">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h2 className="text-xs font-bold uppercase tracking-wide text-stone-400">Projection de trésorerie</h2>
            <div className="flex bg-stone-100 rounded-full p-0.5" data-testid="pilotage-proj-horizon">
              {[[3, "3 mois"], [6, "6 mois"], [12, "Année"]].map(([h, l]) => (
                <button key={h} onClick={() => setProjHorizon(h)} data-testid={`pilotage-proj-${h}`} className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${projHorizon === h ? "bg-[var(--ppd)] text-white" : "text-stone-500"}`}>{l}</button>
              ))}
            </div>
          </div>
          <svg viewBox="0 0 400 100" className="w-full h-32" data-testid="pilotage-projection">
            <line x1="0" y1={pointY(0)} x2="400" y2={pointY(0)} stroke="#e7e5e4" strokeDasharray="4"/>
            <polyline fill="none" stroke="var(--ppd)" strokeWidth="2.5" points={proj.map((p, i) => `${px(i)},${pointY(p.tresorerie)}`).join(" ")}/>
            {proj.map((p, i) => {
              const showVal = proj.length <= 6 || i === 0 || i === proj.length - 1 || (i + 1) % 3 === 0;
              return (
                <g key={i}>
                  <circle cx={px(i)} cy={pointY(p.tresorerie)} r={proj.length > 8 ? 3 : 4} fill={p.tresorerie < 0 ? "#e11d48" : "var(--ppd)"}/>
                  <text x={px(i)} y="99" fontSize="7.5" textAnchor="middle" fill="#78716c">{p.mois}m</text>
                  {showVal && <text x={px(i)} y={pointY(p.tresorerie) - 8} fontSize="8.5" fontWeight="bold" textAnchor="middle" fill={p.tresorerie < 0 ? "#e11d48" : "#1c1917"}>{EUR(p.tresorerie)}</text>}
                </g>
              );
            })}
          </svg>
          <p className="text-[11px] text-stone-400">Trésorerie actuelle + devis validés en attente de paiement − charges mensuelles cumulées − charges ponctuelles prévues.</p>
        </section>

        {/* COÛT DE L'ENTREPRISE */}
        <section>
          <h2 className="text-xs font-bold uppercase tracking-wide text-stone-400 mb-2">Coût de l'entreprise — <span className="text-stone-700 font-bold">{EUR(cockpit.cout_entreprise.total_mensuel)}/mois</span></h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="pilotage-couts">
            {CATS.map(cat => { const Ic = CAT_ICONS[cat]; return (
              <div key={cat} className="bg-white border border-stone-200 rounded-xl p-3 text-center">
                <Ic size={18} className="mx-auto text-[var(--ppd)] mb-1"/>
                <div className="text-[11px] text-stone-500">{cat}</div>
                <div className="font-bold text-sm">{EUR(cockpit.cout_entreprise.par_categorie[cat])}</div>
              </div>
            ); })}
          </div>
        </section>

        {/* POINT D'ÉQUILIBRE */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-white border border-stone-200 rounded-xl p-4" data-testid="pilotage-equilibre">
            <div className="text-xs text-stone-500">CA minimum mensuel</div>
            <div className="text-2xl font-bold text-rose-600">{EUR(cockpit.point_equilibre.ca_min_mensuel)}</div>
            <div className="text-[11px] text-stone-400" data-testid="pilotage-equilibre-detail">
              charges {EUR(cockpit.point_equilibre.cout_charges)}
              {cockpit.point_equilibre.cout_gestion_mensuel > 0 && <> + gestion {EUR(cockpit.point_equilibre.cout_gestion_mensuel)}</>}
            </div>
          </div>
          <div className="bg-white border border-stone-200 rounded-xl p-4">
            <div className="text-xs text-stone-500">CA prévisionnel mensuel</div>
            <div className="text-2xl font-bold text-blue-600">{EUR(cockpit.point_equilibre.ca_previsionnel_mensuel)}</div>
            <div className="text-[11px] text-stone-400">reste à encaisser + signé</div>
          </div>
          <div className={`rounded-xl p-4 border ${cockpit.point_equilibre.marge_manoeuvre >= 0 ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
            <div className="text-xs text-stone-500">Marge de manœuvre</div>
            <div className={`text-2xl font-bold ${cockpit.point_equilibre.marge_manoeuvre >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{EUR(cockpit.point_equilibre.marge_manoeuvre)}</div>
            <div className="text-[11px] text-stone-400">prévisionnel − minimum</div>
          </div>
        </section>

        {/* À ANTICIPER */}
        <section className="bg-white border border-stone-200 rounded-xl p-4">
          <h2 className="text-xs font-bold uppercase tracking-wide text-stone-400 mb-2 flex items-center gap-1.5"><AlertTriangle size={14} className="text-amber-500"/> À anticiper</h2>
          {cockpit.a_anticiper.length === 0 ? <p className="text-sm text-stone-400">Aucune grosse échéance datée. Ajoutez des dates d'échéance à vos charges (TVA, IS, CFE…) pour les voir apparaître.</p> : (
            <div className="space-y-1" data-testid="pilotage-anticiper">
              {cockpit.a_anticiper.map((a, i) => (
                <div key={i} className="flex items-center justify-between text-sm border-b border-stone-50 py-1">
                  <span>{a.label} <span className="text-xs text-stone-400">· {new Date(a.echeance).toLocaleDateString("fr-FR")}</span></span>
                  <span className="font-semibold text-rose-600">{EUR(a.montant)}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* CHARGES PONCTUELLES — IMPORT FACTURE PDF */}
        <section
          className={`bg-white border-2 border-dashed rounded-xl p-4 transition-colors ${dragOver ? "border-[var(--ppd)] bg-orange-50/40" : "border-stone-300"}`}
          data-testid="pilotage-facture-dropzone"
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); uploadFacture(e.dataTransfer.files?.[0]); }}
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2.5 text-sm text-stone-600">
              <FileUp size={20} className="text-[var(--ppd)]"/>
              <div>
                <div className="font-semibold">Charges ponctuelles — facture fournisseur</div>
                <div className="text-[11px] text-stone-400">Glissez un PDF ici : l'IA lit le fournisseur, le montant TTC et l'échéance, puis vous validez.</div>
              </div>
            </div>
            <label className="bg-stone-900 text-white px-3 py-2 rounded text-sm font-semibold flex items-center gap-2 cursor-pointer" data-testid="pilotage-facture-btn">
              {uploadingFacture ? <RefreshCw size={14} className="animate-spin"/> : <FileUp size={14}/>}
              {uploadingFacture ? "Analyse…" : "Choisir un PDF"}
              <input type="file" accept="application/pdf" className="hidden" disabled={uploadingFacture} onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; uploadFacture(f); }} data-testid="pilotage-facture-input"/>
            </label>
          </div>
        </section>

        {/* CHARGES RÉCURRENTES */}
        <section className="bg-white border border-stone-200 rounded-xl">
          <button onClick={() => setShowCharges(v => !v)} className="w-full flex items-center justify-between p-4" data-testid="pilotage-charges-toggle">
            <span className="font-display font-bold flex items-center gap-2">{showCharges ? <ChevronDown size={18}/> : <ChevronRight size={18}/>} Charges mensuelles fixes ({charges.filter(c => c.frequence !== "ponctuel").length})</span>
            <span className="text-xs text-stone-400">Charges récurrentes (mensuel · trimestriel · annuel) mensualisées</span>
          </button>
          {showCharges && (
            <div className="p-4 pt-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-stone-400 border-b"><th className="py-1">Nom</th><th>Catégorie</th><th className="text-right">Montant</th><th>Fréquence</th><th>Échéance</th><th>Type</th><th>Statut</th><th></th></tr></thead>
                <tbody>
                  {charges.filter(c => c.frequence !== "ponctuel").map(c => (
                    <tr key={c.id} className="border-b border-stone-50" data-testid="pilotage-charge-row">
                      <td className="pr-2"><div className="flex items-center gap-1">
                        <input value={c.nom} onChange={e => updCharge(c.id, "nom", e.target.value)} onBlur={() => saveCharge(c)} className="w-full border border-transparent hover:border-stone-200 rounded px-1 py-0.5" data-testid="pilotage-charge-nom"/>
                        {c.pdf_storage_path && <button onClick={() => viewFacture(c.id)} title="Voir la facture" data-testid={`pilotage-charge-facture-${c.id}`} className="text-stone-400 hover:text-[var(--ppd)] shrink-0"><Paperclip size={13}/></button>}
                      </div></td>
                      <td><select value={c.categorie} onChange={e => { updCharge(c.id, "categorie", e.target.value); saveCharge({ ...c, categorie: e.target.value }); }} className="border border-stone-200 rounded px-1 py-0.5 text-xs">{CATS.map(x => <option key={x}>{x}</option>)}</select></td>
                      <td className="text-right"><input type="number" value={c.montant} onChange={e => updCharge(c.id, "montant", Number(e.target.value))} onBlur={() => saveCharge(c)} className="w-20 border border-stone-200 rounded px-1 py-0.5 text-right" data-testid="pilotage-charge-montant"/></td>
                      <td><select value={c.frequence} onChange={e => { updCharge(c.id, "frequence", e.target.value); saveCharge({ ...c, frequence: e.target.value }); }} className="border border-stone-200 rounded px-1 py-0.5 text-xs">{REC_FREQS.map(x => <option key={x}>{x}</option>)}</select></td>
                      <td><input type="date" value={c.prochaine_echeance ? String(c.prochaine_echeance).slice(0, 10) : ""} onChange={e => updCharge(c.id, "prochaine_echeance", e.target.value)} onBlur={() => saveCharge(c)} className="border border-stone-200 rounded px-1 py-0.5 text-xs" data-testid="pilotage-charge-echeance"/></td>
                      <td><select value={c.type} onChange={e => { updCharge(c.id, "type", e.target.value); saveCharge({ ...c, type: e.target.value }); }} className="border border-stone-200 rounded px-1 py-0.5 text-xs"><option value="fixe">Fixe</option><option value="variable">Variable</option></select></td>
                      <td><select value={c.statut} onChange={e => { updCharge(c.id, "statut", e.target.value); saveCharge({ ...c, statut: e.target.value }); }} className={`rounded px-1 py-0.5 text-[11px] font-semibold ${STATUTS[c.statut]?.c}`} data-testid="pilotage-charge-statut">{Object.entries(STATUTS).map(([k, v]) => <option key={k} value={k}>{v.l}</option>)}</select></td>
                      <td><button onClick={() => delCharge(c.id)} className="text-stone-300 hover:text-rose-600" data-testid="pilotage-charge-delete"><Trash2 size={14}/></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button onClick={addCharge} className="mt-2 text-sm text-[var(--ppd)] font-semibold flex items-center gap-1" data-testid="pilotage-add-charge"><Plus size={14}/> Ajouter une charge récurrente</button>
            </div>
          )}
        </section>

        {/* CHARGES PONCTUELLES */}
        <section className="bg-white border border-stone-200 rounded-xl">
          <button onClick={() => setShowPonctuelles(v => !v)} className="w-full flex items-center justify-between p-4" data-testid="pilotage-ponctuelles-toggle">
            <span className="font-display font-bold flex items-center gap-2">{showPonctuelles ? <ChevronDown size={18}/> : <ChevronRight size={18}/>} Charges ponctuelles ({charges.filter(c => c.frequence === "ponctuel").length})</span>
            <span className="text-xs text-stone-400">Dépenses non récurrentes comptées à leur date d'échéance</span>
          </button>
          {showPonctuelles && (
            <div className="p-4 pt-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-stone-400 border-b"><th className="py-1">Nom</th><th>Catégorie</th><th className="text-right">Montant</th><th>Échéance</th><th>Type</th><th>Statut</th><th></th></tr></thead>
                <tbody>
                  {charges.filter(c => c.frequence === "ponctuel").map(c => (
                    <tr key={c.id} className="border-b border-stone-50" data-testid="pilotage-ponctuelle-row">
                      <td className="pr-2"><div className="flex items-center gap-1">
                        <input value={c.nom} onChange={e => updCharge(c.id, "nom", e.target.value)} onBlur={() => saveCharge(c)} className="w-full border border-transparent hover:border-stone-200 rounded px-1 py-0.5" data-testid="pilotage-ponctuelle-nom"/>
                        {c.pdf_storage_path && <button onClick={() => viewFacture(c.id)} title="Voir la facture" data-testid={`pilotage-ponctuelle-facture-${c.id}`} className="text-stone-400 hover:text-[var(--ppd)] shrink-0"><Paperclip size={13}/></button>}
                      </div></td>
                      <td><select value={c.categorie} onChange={e => { updCharge(c.id, "categorie", e.target.value); saveCharge({ ...c, categorie: e.target.value }); }} className="border border-stone-200 rounded px-1 py-0.5 text-xs">{CATS.map(x => <option key={x}>{x}</option>)}</select></td>
                      <td className="text-right"><input type="number" value={c.montant} onChange={e => updCharge(c.id, "montant", Number(e.target.value))} onBlur={() => saveCharge(c)} className="w-20 border border-stone-200 rounded px-1 py-0.5 text-right" data-testid="pilotage-ponctuelle-montant"/></td>
                      <td><input type="date" value={c.prochaine_echeance ? String(c.prochaine_echeance).slice(0, 10) : ""} onChange={e => updCharge(c.id, "prochaine_echeance", e.target.value)} onBlur={() => saveCharge(c)} className="border border-stone-200 rounded px-1 py-0.5 text-xs" data-testid="pilotage-ponctuelle-echeance"/></td>
                      <td><select value={c.type} onChange={e => { updCharge(c.id, "type", e.target.value); saveCharge({ ...c, type: e.target.value }); }} className="border border-stone-200 rounded px-1 py-0.5 text-xs"><option value="fixe">Fixe</option><option value="variable">Variable</option></select></td>
                      <td><select value={c.statut} onChange={e => { updCharge(c.id, "statut", e.target.value); saveCharge({ ...c, statut: e.target.value }); }} className={`rounded px-1 py-0.5 text-[11px] font-semibold ${STATUTS[c.statut]?.c}`} data-testid="pilotage-ponctuelle-statut">{Object.entries(STATUTS).map(([k, v]) => <option key={k} value={k}>{v.l}</option>)}</select></td>
                      <td><button onClick={() => delCharge(c.id)} className="text-stone-300 hover:text-rose-600" data-testid="pilotage-ponctuelle-delete"><Trash2 size={14}/></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button onClick={addPonctuelle} className="mt-2 text-sm text-[var(--ppd)] font-semibold flex items-center gap-1" data-testid="pilotage-add-ponctuelle"><Plus size={14}/> Ajouter une charge ponctuelle</button>
            </div>
          )}
        </section>

      </>)}

      {factureImport && (
        <FactureImportModal
          data={factureImport}
          entite={entite}
          onClose={() => setFactureImport(null)}
          onDone={() => { setFactureImport(null); load(); loadCharges(); toast.success("Charge ponctuelle ajoutée"); }}
        />
      )}
    </div>
  );
}

function Big({ label, icon: Ic, value, color, testid }) {
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-xs text-stone-500 mb-1"><Ic size={14}/> {label}</div>
      <div className={`text-2xl font-bold ${color}`}>{EUR(value)}</div>
    </div>
  );
}


const GESTION_TYPES = [
  { id: "admin_ppd", label: "Administratif" },
  { id: "reunion", label: "Réunion" },
  { id: "commercial", label: "Commercial" },
];
const GESTION_TYPE_LABEL = Object.fromEntries(GESTION_TYPES.map(t => [t.id, t.label]));
const GESTION_CATS = GESTION_TYPES.map(t => t.id);
const fmtDateFr = (d) => { const dt = new Date(String(d || "").slice(0, 10)); return isNaN(dt) ? "—" : dt.toLocaleDateString("fr-FR"); };
const fmtH = (h) => `${Number(h || 0).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} h`;

function GestionEntreprise({ gestion, onChanged }) {
  const [entries, setEntries] = useState([]);
  const [showDetail, setShowDetail] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ category: "admin_ppd", hours: "1", date: new Date().toISOString().slice(0, 10) });

  const load = useCallback(async () => {
    const { data } = await api.get("/time");
    const mine = (data || []).filter(e => !e.project_id && GESTION_CATS.includes(e.category));
    mine.sort((a, b) => String(b.date).localeCompare(String(a.date)));
    setEntries(mine);
  }, []);
  useEffect(() => { load(); }, [load]);

  const now = new Date();
  const ym = now.toISOString().slice(0, 7);
  const start3m = new Date(now.getFullYear(), now.getMonth() - 2, 1);
  const monthEntries = entries.filter(e => String(e.date || "").slice(0, 7) === ym);
  const hMois = monthEntries.reduce((s, e) => s + (e.duration_min || 0) / 60, 0);
  const taux = Number(gestion?.taux_horaire || 0);
  const h3m = entries.filter(e => { const d = new Date(String(e.date || "").slice(0, 10)); return !isNaN(d) && d >= start3m; }).reduce((s, e) => s + (e.duration_min || 0) / 60, 0);

  const resetForm = () => { setEditingId(null); setForm({ category: "admin_ppd", hours: "1", date: new Date().toISOString().slice(0, 10) }); };
  const submit = async () => {
    const h = parseFloat(String(form.hours).replace(",", "."));
    if (!h || h <= 0) { toast.error("Indiquez un nombre d'heures valide"); return; }
    const payload = { category: form.category, project_id: null, duration_min: Math.round(h * 60), date: form.date, notes: "Gestion d'entreprise" };
    if (editingId) { await api.put(`/time/${editingId}`, payload); toast.success("Ligne modifiée"); }
    else { await api.post("/time", payload); toast.success(`+${h} h enregistrée`); }
    resetForm(); await load(); onChanged?.();
  };
  const quick = async (h) => {
    await api.post("/time", { category: "admin_ppd", project_id: null, duration_min: Math.round(h * 60), date: new Date().toISOString().slice(0, 10), notes: "Gestion d'entreprise" });
    toast.success(`+${h} h enregistrée`); await load(); onChanged?.();
  };
  const startEdit = (e) => { setEditingId(e.id); setForm({ category: e.category, hours: String((e.duration_min || 0) / 60), date: String(e.date || "").slice(0, 10) }); setShowDetail(true); };
  const del = async (id) => { if (!window.confirm("Supprimer cette ligne ?")) return; await api.delete(`/time/${id}`); if (editingId === id) resetForm(); await load(); onChanged?.(); };

  return (
    <section className="bg-white border border-stone-200 rounded-xl p-4" data-testid="pilotage-gestion">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xs font-bold uppercase tracking-wide text-stone-400 mb-1 flex items-center gap-1.5"><Clock size={14}/> Temps de gestion d'entreprise</h2>
          <p className="text-[11px] text-stone-400 max-w-md">KPI essentiel — temps passé à piloter l'entreprise (administratif, réunion, commercial), hors production projet. Le temps projet se saisit dans chaque fiche projet.</p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-[var(--ppd)]" data-testid="pilotage-gestion-total">{fmtH(hMois)}</div>
          <div className="text-[11px] text-stone-400">ce mois · {monthEntries.length} saisie{monthEntries.length > 1 ? "s" : ""}</div>
        </div>
      </div>

      {/* Saisie détaillée */}
      <div className="mt-3 flex items-end gap-2 flex-wrap" data-testid="pilotage-gestion-form">
        <label className="flex flex-col gap-0.5">
          <span className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">Jour</span>
          <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="border border-stone-300 rounded px-2 py-1.5 text-sm" data-testid="pilotage-gestion-date"/>
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">Type</span>
          <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="border border-stone-300 rounded px-2 py-1.5 text-sm" data-testid="pilotage-gestion-type">
            {GESTION_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">Heures</span>
          <input type="number" step="0.5" min="0" value={form.hours} onChange={e => setForm({ ...form, hours: e.target.value })} placeholder="1,5" className="w-24 border border-stone-300 rounded px-2 py-1.5 text-sm font-mono" data-testid="pilotage-gestion-hours"/>
        </label>
        <button onClick={submit} data-testid="pilotage-gestion-submit" className="bg-stone-900 text-white rounded px-3 py-1.5 text-sm font-semibold flex items-center gap-1">
          {editingId ? <><Save size={14}/> Mettre à jour</> : <><Plus size={14}/> Ajouter</>}
        </button>
        {editingId && <button onClick={resetForm} data-testid="pilotage-gestion-cancel" className="text-sm text-stone-500 border border-stone-200 rounded px-3 py-1.5">Annuler</button>}
        <span className="flex items-center gap-1 ml-1">
          {[0.5, 1, 2, 4].map(h => (
            <button key={h} onClick={() => quick(h)} data-testid={`pilotage-gestion-quick-${String(h).replace(".", "_")}`} className="text-xs border border-stone-300 rounded-full px-2.5 py-1.5 hover:bg-[var(--ppd)] hover:text-white hover:border-[var(--ppd)] font-semibold transition">+{h} h</button>
          ))}
        </span>
      </div>

      {/* Stats + taux calculé */}
      <div className="mt-3 pt-3 border-t border-stone-100 flex items-center gap-5 flex-wrap text-xs text-stone-500">
        <div>3 mois glissants : <span className="font-bold text-stone-800" data-testid="pilotage-gestion-3m">{fmtH(h3m)}</span></div>
        <div>Coût estimé (3 mois) : <span className="font-bold text-rose-600" data-testid="pilotage-gestion-cost">{EUR(h3m * taux)}</span></div>
        <div title="Somme des charges mensuelles ÷ 169 h">Taux horaire : <span className="font-bold text-stone-800" data-testid="pilotage-gestion-taux">{EUR(taux)}/h</span> <span className="text-stone-400">(charges ÷ 169)</span></div>
        <button onClick={() => setShowDetail(v => !v)} data-testid="pilotage-gestion-detail-toggle" className="ml-auto flex items-center gap-1 font-semibold text-[var(--ppd)]">
          {showDetail ? <ChevronDown size={14}/> : <ChevronRight size={14}/>} Détail ({entries.length})
        </button>
      </div>

      {showDetail && (
        <div className="mt-3 overflow-x-auto" data-testid="pilotage-gestion-detail">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-stone-400 border-b"><th className="py-1">Jour</th><th>Type</th><th className="text-right">Heures</th><th className="text-right">Coût</th><th></th></tr></thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.id} className="border-b border-stone-50" data-testid="pilotage-gestion-row">
                  <td className="py-1.5">{fmtDateFr(e.date)}</td>
                  <td><span className="pill bg-stone-100 text-stone-700 text-[10px]">{GESTION_TYPE_LABEL[e.category] || e.category}</span></td>
                  <td className="text-right font-mono">{fmtH((e.duration_min || 0) / 60)}</td>
                  <td className="text-right font-mono text-stone-500">{EUR(((e.duration_min || 0) / 60) * taux)}</td>
                  <td className="text-right whitespace-nowrap">
                    <button onClick={() => startEdit(e)} data-testid={`pilotage-gestion-edit-${e.id}`} className="text-stone-400 hover:text-[var(--ppd)] mr-2" title="Modifier"><Pencil size={13}/></button>
                    <button onClick={() => del(e.id)} data-testid={`pilotage-gestion-del-${e.id}`} className="text-stone-300 hover:text-rose-600" title="Supprimer"><Trash2 size={13}/></button>
                  </td>
                </tr>
              ))}
              {entries.length === 0 && <tr><td colSpan={5} className="py-4 text-center text-stone-400 italic">Aucune saisie de gestion.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}


function FactureImportModal({ data, entite, onClose, onDone }) {
  const ex = data.extracted || {};
  const [f, setF] = useState({
    fournisseur: ex.fournisseur || "",
    montant: ex.montant_ttc || ex.montant_ht || 0,
    date_echeance: String(ex.date_echeance || ex.date_facture || "").slice(0, 10),
    description: ex.description || "",
    categorie: "Fonctionnement",
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF(p => ({ ...p, [k]: v }));
  const confirm = async () => {
    if (!f.montant || Number(f.montant) <= 0) { toast.error("Montant TTC requis"); return; }
    if (!f.date_echeance) { toast.error("Date d'échéance requise"); return; }
    setSaving(true);
    try {
      await api.post("/pilotage/charges/import-facture/confirm", { import_id: data.import_id, entite, ...f, montant: Number(f.montant) });
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Création impossible"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 max-w-lg w-full" onClick={e => e.stopPropagation()} data-testid="pilotage-facture-modal">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-xl font-bold flex items-center gap-2"><FileUp size={18}/> Charge ponctuelle — validation</h3>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <p className="text-xs text-stone-500 mb-4">Vérifiez les données extraites de <span className="font-medium">{data.filename}</span> avant de créer la charge.</p>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <label className="flex flex-col col-span-2">
            <span className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">Fournisseur</span>
            <input value={f.fournisseur} onChange={e => upd("fournisseur", e.target.value)} data-testid="facture-fournisseur" className="border border-stone-300 rounded px-3 py-2 mt-1"/>
          </label>
          <label className="flex flex-col col-span-2">
            <span className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">Description</span>
            <input value={f.description} onChange={e => upd("description", e.target.value)} data-testid="facture-description" className="border border-stone-300 rounded px-3 py-2 mt-1"/>
          </label>
          <label className="flex flex-col">
            <span className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">Montant TTC (€)</span>
            <input type="number" step="0.01" value={f.montant} onChange={e => upd("montant", e.target.value)} data-testid="facture-montant" className="border border-stone-300 rounded px-3 py-2 mt-1 font-mono"/>
          </label>
          <label className="flex flex-col">
            <span className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">Date d'échéance</span>
            <input type="date" value={f.date_echeance} onChange={e => upd("date_echeance", e.target.value)} data-testid="facture-echeance" className="border border-stone-300 rounded px-3 py-2 mt-1"/>
          </label>
          <label className="flex flex-col col-span-2">
            <span className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">Catégorie</span>
            <select value={f.categorie} onChange={e => upd("categorie", e.target.value)} data-testid="facture-categorie" className="border border-stone-300 rounded px-3 py-2 mt-1">
              {CATS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
        </div>
        <div className="flex gap-2 mt-5 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded">Annuler</button>
          <button onClick={confirm} disabled={saving} data-testid="facture-confirm" className="px-4 py-2 text-sm bg-[var(--ppd)] text-white rounded font-semibold flex items-center gap-2 disabled:opacity-50">
            <Check size={14}/> {saving ? "Création…" : "Créer la charge"}
          </button>
        </div>
      </div>
    </div>
  );
}
